import type { QuestionnaireAutomationDraft } from '@/lib/preVisitQuestionnaireTiming';

export const BOOKING_TIMINGS = ['all_upfront', 'allow_unbooked'] as const;
export type BookingTiming = (typeof BOOKING_TIMINGS)[number];

export const REMAINING_STEPS_BOOKABLE_BY = ['staff_only', 'staff_and_patient'] as const;
export type RemainingStepsBookableBy = (typeof REMAINING_STEPS_BOOKABLE_BY)[number];

export const BILLING_MODES = ['per_step', 'package'] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export const INTERVAL_UNITS = ['days', 'weeks'] as const;
export type IntervalUnit = (typeof INTERVAL_UNITS)[number];

export const PRACTITIONER_MODES = ['inherit', 'all', 'selected'] as const;
export type TreatmentCoursePractitionerMode = (typeof PRACTITIONER_MODES)[number];

export const RESOURCE_MODES = ['inherit', 'override'] as const;
export type TreatmentCourseResourceMode = (typeof RESOURCE_MODES)[number];

export const QUESTIONNAIRE_MODES = ['inherit', 'inherit_plus', 'replace'] as const;
export type TreatmentCourseQuestionnaireMode = (typeof QUESTIONNAIRE_MODES)[number];

export type TreatmentCourseResourceRequirementDraft = {
  resource_group_id: string;
  quantity: number;
  eligibility_mode: 'any' | 'selected';
  eligible_resource_ids: string[];
};

export type TreatmentCourseTemplateStepDraft = {
  clientId: string;
  /** Template step position. Used to read Tilladte for trin N after step ids are reminted. */
  sort_order?: number;
  service_id: string;
  title: string;
  interval_value: number;
  interval_unit: IntervalUnit;
  interval_tolerance_value: number;
  interval_tolerance_unit: IntervalUnit;
  duration_minutes: number | null;
  practitioner_mode: TreatmentCoursePractitionerMode;
  practitioner_ids: string[];
  resource_mode: TreatmentCourseResourceMode;
  resource_requirements: TreatmentCourseResourceRequirementDraft[];
  questionnaire_mode: TreatmentCourseQuestionnaireMode;
  automations: QuestionnaireAutomationDraft[];
  allowed_forms_template_ids: string[];
};

export type TreatmentCourseTemplateDraft = {
  id?: string;
  catalog_service_id?: string | null;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  booking_timing: BookingTiming;
  remaining_steps_bookable_by: RemainingStepsBookableBy;
  billing_mode: BillingMode;
  package_price_cents: number | null;
  steps: TreatmentCourseTemplateStepDraft[];
};

export type TreatmentCourseTemplateReplacePayload = {
  id?: string;
  catalog_service_id?: string | null;
  name: string;
  description: string | null;
  color: string;
  is_active: boolean;
  sort_order: number;
  booking_timing: BookingTiming;
  remaining_steps_bookable_by: RemainingStepsBookableBy;
  billing_mode: BillingMode;
  package_price_cents: number | null;
  steps: TreatmentCourseTemplateStepReplacePayload[];
};

export type TreatmentCourseTemplateStepReplacePayload = {
  sort_order: number;
  service_id: string;
  title: string | null;
  interval_value: number;
  interval_unit: IntervalUnit;
  interval_tolerance_value: number;
  interval_tolerance_unit: IntervalUnit;
  duration_minutes: number | null;
  practitioner_mode: TreatmentCoursePractitionerMode;
  practitioner_ids: string[];
  resource_mode: TreatmentCourseResourceMode;
  resource_requirements: TreatmentCourseResourceRequirementReplacePayload[];
  questionnaire_mode: TreatmentCourseQuestionnaireMode;
  automations: TreatmentCourseAutomationReplacePayload[];
  allowed_forms_template_ids: string[];
};

export type TreatmentCourseResourceRequirementReplacePayload = {
  resource_group_id: string;
  quantity: number;
  eligibility_mode: 'any' | 'selected';
  resource_ids: string[];
};

export const BOOKED_COURSE_STATUSES = ['active', 'completed', 'cancelled'] as const;
export type BookedCourseStatus = (typeof BOOKED_COURSE_STATUSES)[number];

export const BOOKED_STEP_STATUSES = [
  'unscheduled',
  'upcoming',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type BookedStepStatus = (typeof BOOKED_STEP_STATUSES)[number];

export type BookedCourseIntervalWarning = {
  step_id: string;
  sort_order: number;
  previous_step_id: string | null;
  previous_starts_at: string | null;
  this_starts_at: string | null;
  window_start: string | null;
  window_end: string | null;
  is_broken: boolean;
};

export type BookedTreatmentCourseStep = {
  id: string;
  sort_order: number;
  template_step_id: string | null;
  appointment_id: string | null;
  service_id: string | null;
  service_name: string;
  title: string | null;
  interval_value: number;
  interval_unit: IntervalUnit;
  interval_tolerance_value: number;
  interval_tolerance_unit: IntervalUnit;
  duration_minutes: number;
  practitioner_mode: TreatmentCoursePractitionerMode;
  resource_mode: TreatmentCourseResourceMode;
  questionnaire_mode: TreatmentCourseQuestionnaireMode;
  status: BookedStepStatus;
  starts_at: string | null;
  ends_at: string | null;
  practitioner_ids: string[];
  resource_ids: string[];
  automations: QuestionnaireAutomationDraft[];
  allowed_forms_template_ids: string[];
};

export type BookedTreatmentCourse = {
  id: string;
  clinic_id: string;
  patient_id: string;
  template_id: string | null;
  template_name: string;
  status: BookedCourseStatus;
  booking_timing: BookingTiming;
  remaining_steps_bookable_by: RemainingStepsBookableBy;
  billing_mode: BillingMode;
  package_price_cents: number | null;
  interval_warnings: BookedCourseIntervalWarning[];
  steps: BookedTreatmentCourseStep[];
  /** Frozen template JSON from booking. Used to recover a missed Tilladte copy. */
  template_snapshot?: unknown;
  /**
   * Generelt Tilladte, when a future change stores them. Absent or null means
   * the field does not exist yet. Do not invent a list in settings.
   */
  general_allowed_forms_template_ids?: string[] | null;
};

export type TreatmentCourseAutomationReplacePayload = {
  forms_template_id: string;
  phase: QuestionnaireAutomationDraft['phase'];
  offset_minutes: number;
  delay_after_booking_minutes: number;
  is_active: boolean;
  sort_order: number;
};
